import { type FC } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Image from '@segment/matcha/components/Image'
import Button from '@segment/matcha/components/Button'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { trackButtonClick } from '@segment/tracking/scripts'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import artificialIntelligencePng2x from './assets/ArtificialIntelligence@2x.png'
import artificialIntelligenceWebp from './assets/ArtificialIntelligence.webp'
import artificialIntelligenceWebp2x from './assets/ArtificialIntelligence@2x.webp'

const copy = {
  heading: `Artificial Intelligence`,
  description: `Segment makes it easier than ever to understand, predict, and generate 1:1 customer engagement in real time and at scale.`,
}

const data = [
  {
    heading: `Predictive AI`,
    description: [
      `Predict customer behavior using a no-code builder to power more timely, targeted, and precise personalized experiences on every channel.`,
      `Available with Unify Plus and Engage plans.`,
    ],
  },
  {
    heading: `Generative AI for Marketers`,
    description: [
      `Create and launch complex audiences in minutes with a simple text prompt.`,
      `Available with Engage plans.`,
    ],
  },
  {
    heading: `Generative AI for Developers`,
    description: [
      `Generate and optimize code in minutes from natural language prompts (with rETL support for Functions) to create custom integrations and capabilities faster than ever.`,
      `Available with all CDP and Connections Business plans.`,
    ],
  },
]

const demoButton = {
  text: `Get a demo`,
  url: `/demo/`,
}

const learnMoreButton = {
  text: `Learn more about Customer AI`,
  url: `/solutions/ai/`,
}

const ArtificialIntelligence: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <StyledCopyTextBase centered={true}>
            <CopyText.Heading variant="h2" tag="h2">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description variant="pLarge" tag="p">
              {copy.description}
            </CopyText.Description>
          </StyledCopyTextBase>
        </Section.Copy>

        <Section.Contents>
          <ColumnsWrapper>
            <ColumnTrack columns={1} overrides={{ md: 2 }}>
              <Content>
                {data.map(({ heading, description }) => (
                  <FeatureBoxBase key={heading} alignment="left">
                    <FeatureBox.Body>
                      <FeatureBox.Title>{heading}</FeatureBox.Title>
                      {description.map((text) => (
                        <FeatureBox.Text key={text} variant="pMedium">
                          {text}
                        </FeatureBox.Text>
                      ))}
                    </FeatureBox.Body>
                  </FeatureBoxBase>
                ))}
              </Content>

              <div>
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${artificialIntelligenceWebp}, ${artificialIntelligenceWebp2x} 2x`}
                  />
                  <StyledImage
                    width="935"
                    height="771"
                    src={artificialIntelligencePng2x}
                    alt={`Illustration: ${copy.heading}`}
                    loading="lazy"
                  />
                </picture>
              </div>
            </ColumnTrack>
          </ColumnsWrapper>

          <ButtonsWrapper>
            <Button
              color="primary"
              size="medium"
              variant="filled"
              href={getAbsoluteUrl(demoButton.url, pageConfig)}
              onClick={() =>
                trackButtonClick({
                  buttonText: demoButton.text,
                  targetUrl: demoButton.url,
                  buttonId: `pricing-landing-page-ai-section-demo`,
                })
              }
            >
              {demoButton.text}
            </Button>
            <Button
              color="primary"
              size="medium"
              variant="outlined"
              href={getAbsoluteUrl(learnMoreButton.url, pageConfig)}
              onClick={() =>
                trackButtonClick({
                  buttonText: learnMoreButton.text,
                  targetUrl: learnMoreButton.url,
                  buttonId: `pricing-landing-page-ai-section-learn-more`,
                })
              }
            >
              {learnMoreButton.text}
            </Button>
          </ButtonsWrapper>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledSection = styled(SectionBase)`
  padding-top: 80px;

  ${Section.Wrapper} {
    gap: 0;
  }
`

const StyledCopyTextBase = styled(CopyTextBase)`
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    text-align: left;
  }
`

const ColumnsWrapper = styled.div`
  margin-top: 30px;
`

const Content = styled.div`
  display: grid;
  align-content: center;
  gap: 24px;
  justify-self: center;
`

const StyledImage = styled(Image)`
  width: 100%;
  max-width: 620px;
`

const ButtonsWrapper = styled.div`
  margin-top: 44px;
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    flex-direction: column;
  }
`

export default ArtificialIntelligence
